/**
 * RBAC v2 predicates for Marketing Analyzer (RGI-1042 / RGI-1113).
 *
 * All five predicates short-circuit to `true` for v1 users (no `mkClaims.rbac`
 * claim) and SuperKudu. This is the hard constraint from RGI-1024: legacy
 * users see zero behavioral change at every layer.
 *
 * Inputs are the rbac claim object (or null) — read it once at the call site
 * via `getUserClaims().rbac` so each callsite passes an already-resolved
 * value rather than re-reading localStorage.
 */

export type RbacRole = "orgAdmin" | "standard" | "readOnly";
export type RbacTier = "starter" | "growth" | "enterprise";

export interface RbacClaim {
  role: RbacRole;
  assignedModules: string[];
  tier: RbacTier;
  purchasedModules: string[];
}

const TIER_ORDER: Record<RbacTier, number> = {
  starter: 0,
  growth: 1,
  enterprise: 2,
};

/** Predicate input: rbac claim or null/undefined (v1 user). */
type RbacInput = RbacClaim | null | undefined;

interface BypassFlags {
  /** True when the calling user is SuperKudu (always bypasses). */
  superKudu?: boolean;
}

export function isV2User(rbac: RbacInput): boolean {
  return !!rbac;
}

export function hasModule(
  rbac: RbacInput,
  moduleId: string,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu) return true;
  if (!rbac) return true;
  // A read-only seat is stored with no module assignment (bongo and Argo both
  // write `assigned_modules: []` for the role), yet read-only is Market
  // Analyzer's own role — Admin promises "Read-only users always have Market
  // Analyzer access". Without this the module gate locked every read-only
  // invitee out on acceptance (HIP-12697). The seat only counts when the
  // tenant bought Market Analyzer.
  if (rbac.role === "readOnly" && moduleId === "market_analyzer")
    return rbac.purchasedModules.includes(moduleId);
  return rbac.assignedModules.includes(moduleId);
}

export function tenantPurchased(
  rbac: RbacInput,
  moduleId: string,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu) return true;
  if (!rbac) return true;
  return rbac.purchasedModules.includes(moduleId);
}

export function hasRole(
  rbac: RbacInput,
  requiredRole: RbacRole,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu) return true;
  if (!rbac) return true;
  return rbac.role === requiredRole;
}

export function hasTier(
  rbac: RbacInput,
  minTier: "growth" | "enterprise",
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu) return true;
  if (!rbac) return true;
  return TIER_ORDER[rbac.tier] >= TIER_ORDER[minTier];
}

// App-switcher visibility & "(limited access)" semantics. Admin Hub shows for
// Org Admins or Data Studio customers; Data Studio needs the tenant to have
// purchased it, then shows for Org Admins or module holders (RGI-3158).
// Read-only seats never see Data Studio.
// v1 users / SuperKudu get full access, no badge.

export function canSeeAdminHub(
  rbac: RbacInput,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu || !rbac) return true;
  return (
    rbac.role === "orgAdmin" || rbac.assignedModules.includes("data_studio")
  );
}

export function canSeeDataStudio(
  rbac: RbacInput,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu || !rbac) return true;
  if (rbac.role === "readOnly") return false;
  if (!rbac.purchasedModules.includes("data_studio")) return false;
  // Org Admins invited via Admin are stored with `assignedModules: []`, so the
  // role grants access on its own once the tenant owns the module.
  return (
    rbac.role === "orgAdmin" || rbac.assignedModules.includes("data_studio")
  );
}

/**
 * RBAC tenant that has not purchased Data Studio → shown greyed out, not
 * navigable. Read-only seats get no entry at all, locked or not.
 */
export function dataStudioNotPurchased(
  rbac: RbacInput,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu || !rbac || rbac.role === "readOnly") return false;
  return !rbac.purchasedModules.includes("data_studio");
}

/** Data Studio standard user (has the module, not an Org Admin) → limited admin nav. */
export function adminHubLimited(
  rbac: RbacInput,
  flags: BypassFlags = {}
): boolean {
  if (flags.superKudu || !rbac) return false;
  return (
    rbac.assignedModules.includes("data_studio") && rbac.role !== "orgAdmin"
  );
}
